import { AxiosInstance } from 'axios';
import { Currency } from 'dinero.js';
import baseAxios from '@/helpers/forms/axios';
import { serviceProviderUserMembershipInvitationInterface } from '../users';

export enum ServiceProviderUserRole {
    Owner = 'owner',
    OfficeEmployee = 'office',
    WarehouseEmployee = 'warehouse',
}

export enum Feature {
    orders = 'orders',
    amazonShipments = 'amazon_shipments',
    locations = 'locations',
}

export const serviceProviderPermissions = [
    'createOutboundShipments',
    'outboundShipmentsMerchantCreateAsDraft',
    'arrangeTransport',
    'addAttachments',
    'requireCreditCard',
    'registerMerchants',
    'updateShippedInboundShipmentItems',
    'updateReceivedInboundShipmentItems',
] as const;
export type ServiceProviderPermissionEnum = typeof serviceProviderPermissions[number];

export interface ServiceProviderPreferences {
    inventory: {
        products: {
            traceabilityGroups: {
                enabled: boolean
            }
        }
    }
    clientSidebar: {
        links: Array<{ name: string; url: string }>
    }
}

export interface serviceProviderInterface {
    id: number
    name: string
    subdomainName: string
    domainName: string
    platformName: string
    platformKey: number
    platformDomain: string
    logoFile: string
    daysOfFreeStorage: number
    defaultAutomaticClientBilling: boolean
    supportEmail: string
    faviconFile: string
    stripeAccountId: string
    logoBackground: string
    currency: Currency
    availablePermissions: ServiceProviderPermissionEnum[]
    preferences: ServiceProviderPreferences
    shippingSettings: {
        pick: {
            locationScan: 'required' | 'optional' | 'disabled'
            itemScan: 'required' | 'required_first_pick' | 'optional' | 'disabled'
        }
    }
    viewSettings: {
        metric?: {
            enabled: boolean
        }
        shipments?: {
            addItems?: {
                showExpiryDate?: {
                    enabled: boolean
                    minimumDays?: number
                }
            }
            createInboundShipmentDefault?: {
                enabled: boolean
            }
        }
        items?: {
            showCondition: {
                enabled: boolean
            }
        }
        sounds?: {
            enabled: boolean
        }
        theme?: string
    }
}

export interface registerServiceProviderFormInterface {
    name: string
    subdomainName: string
    currency: string
}

export default (axios: AxiosInstance = baseAxios): { registerServiceProvider: (form: registerServiceProviderFormInterface) => Promise<serviceProviderInterface>; fetchServiceProviderPendingInvitations: () => Promise<serviceProviderUserMembershipInvitationInterface[]>; resendServiceProviderInvitation: (token: string) => Promise<void>; fetchCurrentServiceProvider: () => Promise<serviceProviderInterface>; } => ({
    registerServiceProvider: async (form: registerServiceProviderFormInterface): Promise<serviceProviderInterface> => {
        const response = await axios.post<{ serviceProvider: serviceProviderInterface }>('/api/register-company', { name: form.name, subdomainName: form.subdomainName, currency: form.currency });

        return response.data.serviceProvider;
    },

    fetchServiceProviderPendingInvitations: async (): Promise<serviceProviderUserMembershipInvitationInterface[]> => {
        const response = await axios.get<{ data: serviceProviderUserMembershipInvitationInterface[] }>('/api/serviceProviders/invitations');

        return response.data.data;
    },

    resendServiceProviderInvitation: async (token: string): Promise<void> => {
        await axios.get(`/api/users/invitations/serviceProvider/${token}/resend`);
    },

    fetchCurrentServiceProvider: async (): Promise<serviceProviderInterface> => {
        const response = await axios.get<{ data: serviceProviderInterface }>('/api/narrative/serviceProvider');

        return response.data.data;
    },
});
